import './account.css'
import { reportPageView } from './pageview.js'
import { buildLoginUrl, logout } from './auth-state.js'
import { getLocale, localeHeaderValue, onLocaleChange } from './locale.js'
import { getMessages } from './i18n.js'
import { formatDurationSeconds, formatStorage } from './formatters.js'

const statusEl = document.getElementById('account-status')
const gridEl = document.getElementById('account-grid')
const overviewEl = document.getElementById('account-overview')
const userKv = document.getElementById('account-user-kv')
const planKv = document.getElementById('account-plan-kv')
const quotaKv = document.getElementById('account-quota-kv')
const overviewNameEl = document.getElementById('account-overview-name')
const overviewMetaEl = document.getElementById('account-overview-meta')
const overviewMetricsEl = document.getElementById('account-overview-metrics')
const sessionsBody = document.getElementById('account-sessions-body')
const eventsBody = document.getElementById('account-events-body')
const logoutBtn = document.getElementById('account-logout-btn')
const passwordForm = document.getElementById('account-password-form')
const passwordStatusEl = document.getElementById('account-password-status')
const educationLinkCard = document.getElementById('account-nav-education')
const teamsLinkCard = document.getElementById('account-nav-teams')
const accountSectionButtons = Array.from(document.querySelectorAll('[data-account-section]'))
const accountSectionPanels = Array.from(document.querySelectorAll('[data-account-panel]'))

let accountSummary = null
let accountSecurity = null

function t() {
  return getMessages('pages/account', getLocale())
}

function setStatus(text, isError = false) {
  statusEl.textContent = text
  statusEl.dataset.error = isError ? 'true' : 'false'
}

function setInlineStatus(text, isError = false) {
  if (!passwordStatusEl) return
  passwordStatusEl.textContent = text || ''
  passwordStatusEl.dataset.error = isError ? 'true' : 'false'
}

function getRequestedAccountSection() {
  let hashValue = ''
  try {
    hashValue = decodeURIComponent(window.location.hash.replace(/^#/, '')).trim()
  } catch {
    hashValue = ''
  }
  if (accountSectionButtons.some((button) => button.dataset.accountSection === hashValue)) {
    return hashValue
  }
  return 'overview'
}

function activateAccountSection(sectionId, { updateHash = true } = {}) {
  if (!accountSectionButtons.length || !accountSectionPanels.length) return
  const nextSection = accountSectionButtons.some((button) => button.dataset.accountSection === sectionId)
    ? sectionId
    : 'overview'

  accountSectionButtons.forEach((button) => {
    const isActive = button.dataset.accountSection === nextSection
    button.classList.toggle('is-active', isActive)
    button.setAttribute('aria-selected', isActive ? 'true' : 'false')
  })

  accountSectionPanels.forEach((panel) => {
    const isActive = panel.dataset.accountPanel === nextSection
    panel.hidden = !isActive
    panel.classList.toggle('is-active', isActive)
  })

  if (updateHash) {
    const nextUrl = new URL(window.location.href)
    nextUrl.hash = nextSection === 'overview' ? '' : nextSection
    window.history.replaceState(null, '', nextUrl.toString())
  }
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function formatDate(ts) {
  if (!ts) return '-'
  return new Date(ts * 1000).toLocaleString(getLocale() === 'en' ? 'en-US' : 'zh-CN', { hour12: false })
}

function renderKv(root, rows) {
  root.innerHTML = rows.map(([label, value]) => `
    <div class="account-kv-row">
      <span class="account-kv-label">${escapeHtml(label)}</span>
      <span class="account-kv-value">${escapeHtml(value)}</span>
    </div>
  `).join('')
}

function mapSessionStatus(session) {
  const text = t()
  if (session.revoked_at) return text.sessionRevoked
  if (session.is_current) return text.sessionCurrent
  return text.sessionActive
}

function mapEventType(eventType) {
  const text = t()
  return text.eventTypeLabels?.[eventType] || eventType
}

function renderSessions() {
  const text = t()
  const sessions = accountSecurity?.sessions || []
  if (!sessions.length) {
    sessionsBody.innerHTML = `<tr><td colspan="6" class="account-table-empty">${escapeHtml(text.emptySessions)}</td></tr>`
    return
  }
  sessionsBody.innerHTML = sessions.map((session) => `
    <tr>
      <td>${escapeHtml(session.user_agent || text.unknownDevice)}</td>
      <td>${escapeHtml(session.ip_address || '-')}</td>
      <td>${escapeHtml(formatDate(session.created_at))}</td>
      <td>${escapeHtml(formatDate(session.expires_at))}</td>
      <td><span class="account-badge${session.revoked_at ? ' is-muted' : session.is_current ? ' is-current' : ''}">${escapeHtml(mapSessionStatus(session))}</span></td>
      <td>
        ${session.revoked_at ? '' : `<button class="account-table-action" type="button" data-session-revoke="${escapeHtml(session.id)}" data-session-current="${session.is_current ? 'true' : 'false'}">${escapeHtml(session.is_current ? text.revokeCurrentSession : text.revokeSession)}</button>`}
      </td>
    </tr>
  `).join('')
}

function renderEvents() {
  const text = t()
  const events = accountSecurity?.login_events || []
  if (!events.length) {
    eventsBody.innerHTML = `<tr><td colspan="5" class="account-table-empty">${escapeHtml(text.emptyEvents)}</td></tr>`
    return
  }
  eventsBody.innerHTML = events.map((event) => `
    <tr>
      <td>${escapeHtml(mapEventType(event.event_type))}</td>
      <td>${escapeHtml(event.detail || '-')}</td>
      <td>${escapeHtml(event.ip_address || '-')}</td>
      <td>${escapeHtml(event.user_agent || text.unknownDevice)}</td>
      <td>${escapeHtml(formatDate(event.created_at))}</td>
    </tr>
  `).join('')
}

function renderOverview() {
  if (!accountSummary) return
  const text = t()
  const { user, billing } = accountSummary
  const displayName = user.display_name || user.username || user.email
  const meta = [
    user.email,
    `${text.userMetaLabels[0]} ${formatDate(user.last_login_at)}`,
    `${text.userMetaLabels[1]} ${formatDate(user.created_at)}`,
  ].filter(Boolean)
  overviewNameEl.textContent = displayName
  overviewMetaEl.textContent = meta.join(' · ')
  overviewMetricsEl.innerHTML = [
    [text.overviewMetrics[0], billing.plan.name],
    [text.overviewMetrics[1], billing.plan.billing_cycle],
    [text.overviewMetrics[2], formatDate(billing.subscription.current_period_end)],
    [text.overviewMetrics[3], `${formatDurationSeconds(billing.quota.workspace_active_seconds)} / ${formatDurationSeconds(billing.capabilities?.max_daily_workspace_seconds || 0)}`],
  ].map(([label, value]) => `
    <div class="account-overview-metric">
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(value)}</strong>
    </div>
  `).join('')
  const capabilities = billing.capabilities || {}
  if (educationLinkCard) educationLinkCard.hidden = !capabilities.classroom_management
  if (teamsLinkCard) teamsLinkCard.hidden = !capabilities.team_collaboration
}

async function loadAccountSummary() {
  const res = await fetch('/api/account/summary', { credentials: 'include' })
  if (res.status === 401) {
    window.location.href = buildLoginUrl('/account')
    return false
  }
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(body.detail || t().loadFailed)
  accountSummary = body
  return true
}

async function loadAccountSecurity() {
  const res = await fetch('/api/account/security', {
    credentials: 'include',
    headers: { 'Accept-Language': localeHeaderValue() },
  })
  if (res.status === 401) {
    window.location.href = buildLoginUrl('/account')
    return false
  }
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(body.detail || t().securityLoadFailed)
  accountSecurity = body
  return true
}

function renderAccountSummary() {
  if (!accountSummary) return
  const text = t()
  const { user, billing } = accountSummary
  renderKv(userKv, [
    [text.labelsUser[0], user.email],
    [text.labelsUser[1], user.username || '-'],
    [text.labelsUser[2], user.display_name || '-'],
    [text.labelsUser[3], user.role],
    [text.labelsUser[4], user.status],
  ])
  renderKv(planKv, [
    [text.labelsPlan[0], billing.plan.name],
    [text.labelsPlan[1], billing.plan.code],
    [text.labelsPlan[2], billing.plan.billing_cycle],
    [text.labelsPlan[3], `${(billing.plan.price_cents / 100).toFixed(2)} ${billing.plan.currency}`],
    [text.labelsPlan[4], formatDate(billing.subscription.current_period_end)],
  ])
  renderKv(quotaKv, [
    [text.labelsQuota[0], formatDate(billing.quota.period_start)],
    [text.labelsQuota[1], formatDate(billing.quota.period_end)],
    [text.labelsQuota[2], formatDurationSeconds(billing.quota.workspace_active_seconds)],
    [text.labelsQuota[3], formatStorage(billing.quota.storage_bytes)],
  ])
  renderOverview()
}

function applyLocalizedText() {
  const text = t()
  const sectionLabels = text.sections || {}
  document.title = text.title
  document.querySelector('.account-brand-text')?.replaceChildren(document.createTextNode(text.brandName || '天元计算'))
  accountSectionButtons.forEach((button) => {
    const labelEl = button.querySelector('span')
    const label = sectionLabels[button.dataset.accountSection]
    if (labelEl && label) labelEl.textContent = label
  })
  document.getElementById('account-link-workspace').textContent = text.workspace
  document.getElementById('account-link-billing').textContent = text.overviewLinks[0]
  document.getElementById('account-link-docs').textContent = text.overviewLinks[1]
  logoutBtn.textContent = text.logout
  document.getElementById('account-overview-kicker').textContent = text.overviewKicker
  document.getElementById('account-overview-plan-title').textContent = text.overviewPlanTitle
  document.getElementById('account-title-user').textContent = text.headings[0]
  document.getElementById('account-title-plan').textContent = text.headings[1]
  document.getElementById('account-title-quota').textContent = text.headings[2]
  document.getElementById('account-title-links').textContent = text.headings[3]
  document.getElementById('account-title-password').textContent = text.headings[4]
  document.getElementById('account-title-sessions').textContent = text.headings[5]
  document.getElementById('account-title-events').textContent = text.headings[6]
  document.getElementById('account-title-support').textContent = text.headings[7]
  document.getElementById('account-note-sessions').textContent = text.sessionsNote
  document.getElementById('account-note-events').textContent = text.eventsNote
  document.getElementById('account-note-support').textContent = text.supportNote
  document.getElementById('account-password-label-current').textContent = text.passwordLabels[0]
  document.getElementById('account-password-label-next').textContent = text.passwordLabels[1]
  document.getElementById('account-password-label-confirm').textContent = text.passwordLabels[2]
  document.getElementById('account-password-current').placeholder = text.passwordPlaceholders[0]
  document.getElementById('account-password-next').placeholder = text.passwordPlaceholders[1]
  document.getElementById('account-password-confirm').placeholder = text.passwordPlaceholders[2]
  document.getElementById('account-password-submit').textContent = text.passwordSubmit
  document.getElementById('account-session-head-device').textContent = text.sessionHeadings[0]
  document.getElementById('account-session-head-ip').textContent = text.sessionHeadings[1]
  document.getElementById('account-session-head-created').textContent = text.sessionHeadings[2]
  document.getElementById('account-session-head-expire').textContent = text.sessionHeadings[3]
  document.getElementById('account-session-head-status').textContent = text.sessionHeadings[4]
  document.getElementById('account-session-head-action').textContent = text.sessionHeadings[5]
  document.getElementById('account-event-head-type').textContent = text.eventHeadings[0]
  document.getElementById('account-event-head-detail').textContent = text.eventHeadings[1]
  document.getElementById('account-event-head-ip').textContent = text.eventHeadings[2]
  document.getElementById('account-event-head-device').textContent = text.eventHeadings[3]
  document.getElementById('account-event-head-created').textContent = text.eventHeadings[4]
  document.getElementById('account-nav-billing-title').textContent = text.linkCards.billing[0]
  document.getElementById('account-nav-billing-copy').textContent = text.linkCards.billing[1]
  document.getElementById('account-nav-workspace-title').textContent = text.linkCards.workspace[0]
  document.getElementById('account-nav-workspace-copy').textContent = text.linkCards.workspace[1]
  document.getElementById('account-nav-education-title').textContent = text.linkCards.education[0]
  document.getElementById('account-nav-education-copy').textContent = text.linkCards.education[1]
  document.getElementById('account-nav-teams-title').textContent = text.linkCards.teams[0]
  document.getElementById('account-nav-teams-copy').textContent = text.linkCards.teams[1]
  document.getElementById('account-nav-docs-title').textContent = text.linkCards.docs[0]
  document.getElementById('account-nav-docs-copy').textContent = text.linkCards.docs[1]
  document.getElementById('account-nav-feedback-title').textContent = text.linkCards.feedback[0]
  document.getElementById('account-nav-feedback-copy').textContent = text.linkCards.feedback[1]
  document.getElementById('account-support-docs-title').textContent = text.supportCards.docs[0]
  document.getElementById('account-support-docs-copy').textContent = text.supportCards.docs[1]
  document.getElementById('account-support-workspace-title').textContent = text.supportCards.workspace[0]
  document.getElementById('account-support-workspace-copy').textContent = text.supportCards.workspace[1]
  document.getElementById('account-support-feedback-title').textContent = text.supportCards.feedback[0]
  document.getElementById('account-support-feedback-copy').textContent = text.supportCards.feedback[1]
  renderAccountSummary()
  renderSessions()
  renderEvents()
}

async function loadAccountPage() {
  setStatus(t().loading)
  const okSummary = await loadAccountSummary()
  if (!okSummary) return
  const okSecurity = await loadAccountSecurity()
  if (!okSecurity) return
  renderAccountSummary()
  renderSessions()
  renderEvents()
  overviewEl.hidden = false
  gridEl.hidden = false
  activateAccountSection(getRequestedAccountSection(), { updateHash: false })
  setStatus('')
}

logoutBtn?.addEventListener('click', async () => {
  try {
    await logout()
    window.location.href = '/'
  } catch (error) {
    setStatus(error.message || t().logoutFailed, true)
  }
})

passwordForm?.addEventListener('submit', async (event) => {
  event.preventDefault()
  const text = t()
  const currentPassword = document.getElementById('account-password-current').value
  const newPassword = document.getElementById('account-password-next').value
  const confirmPassword = document.getElementById('account-password-confirm').value
  setInlineStatus('')
  if (newPassword !== confirmPassword) {
    setInlineStatus(text.passwordMismatch, true)
    return
  }
  try {
    const res = await fetch('/api/account/change-password', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': localeHeaderValue(),
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    })
    const body = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(body.detail || text.passwordUpdateFailed)
    }
    passwordForm.reset()
    setInlineStatus(body.message || text.passwordUpdated)
    await loadAccountSecurity()
    renderSessions()
    renderEvents()
  } catch (error) {
    setInlineStatus(error.message || text.passwordUpdateFailed, true)
  }
})

sessionsBody?.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-session-revoke]')
  if (!button) return
  const sessionId = button.dataset.sessionRevoke
  const isCurrent = button.dataset.sessionCurrent === 'true'
  const text = t()
  button.disabled = true
  try {
    const res = await fetch(`/api/account/sessions/${sessionId}/revoke`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Accept-Language': localeHeaderValue() },
    })
    const body = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(body.detail || text.sessionRevokeFailed)
    }
    if (isCurrent) {
      window.location.href = buildLoginUrl('/account')
      return
    }
    setStatus(body.message || text.sessionRevokedMessage)
    await loadAccountSecurity()
    renderSessions()
    renderEvents()
  } catch (error) {
    setStatus(error.message || text.sessionRevokeFailed, true)
  } finally {
    button.disabled = false
  }
})

accountSectionButtons.forEach((button) => {
  button.addEventListener('click', () => {
    activateAccountSection(button.dataset.accountSection)
  })
})

window.addEventListener('hashchange', () => {
  activateAccountSection(getRequestedAccountSection(), { updateHash: false })
})

activateAccountSection(getRequestedAccountSection(), { updateHash: false })

applyLocalizedText()
onLocaleChange(() => {
  setStatus('')
  setInlineStatus('')
  applyLocalizedText()
})

reportPageView('/account')
loadAccountPage().catch((error) => {
  setStatus(error.message || t().genericLoadFailed, true)
})
